import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit, Loader2, Plus, Power } from "lucide-react";
import type { WarehouseRecord } from "@/types/inventory";

const blank = { name: "", code: "", address: "", city: "", state: "", notes: "", is_default: false };

export function WarehousePage() {
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("warehouse.manage");
  const [rows, setRows] = useState<WarehouseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [dialog, setDialog] = useState<WarehouseRecord | "new" | null>(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/api/Warehouse/List${showClosed ? "?include_closed=true" : ""}`)
      .then((r) => setRows(unwrapArray<WarehouseRecord>(r)))
      .catch((err: any) => error("Fetch Error", err.message || "Failed to load warehouses"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showClosed]);

  useEffect(() => {
    if (!dialog) return;
    if (dialog === "new") return setForm(blank);
    setForm({ name: dialog.name, code: dialog.code, address: dialog.address || "", city: dialog.city || "", state: dialog.state || "", notes: dialog.notes || "", is_default: dialog.is_default });
  }, [dialog]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return error("Validation Error", "Enter the warehouse name");
    setSaving(true);
    try {
      const body = { name: form.name.trim(), code: form.code.trim() || undefined, address: form.address.trim() || undefined, city: form.city.trim() || undefined, state: form.state.trim() || undefined, notes: form.notes.trim() || undefined, is_default: form.is_default };
      if (dialog && dialog !== "new") await api.put(`/api/Warehouse/Update/${dialog.id}`, body);
      else await api.post("/api/Warehouse/Insert", body);
      success("Warehouse saved", "");
      setDialog(null);
      load();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (w: WarehouseRecord) => {
    try {
      await api.patch(`/api/Warehouse/SetActive/${w.id}`, { active: !w.status });
      load();
    } catch (err: any) {
      error("Could not change", err.message || "Please try again");
    }
  };

  const set = (k: keyof typeof blank) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Warehouses</h1>
          <p className="text-sm text-muted-foreground mt-0.5">The godowns and stores where your stock is kept</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-xs text-muted-foreground inline-flex items-center gap-2">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed
          </label>
          {canManage && (
            <Button size="sm" onClick={() => setDialog("new")} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" />
              Add warehouse
            </Button>
          )}
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <TableSkeleton rows={3} columns={4} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Warehouse</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Rolls held</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((w) => (
                <TableRow key={w.id} className={w.status ? "" : "opacity-60"}>
                  <TableCell>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {w.name}
                      {w.is_default && <Badge variant="blue">Default</Badge>}
                      {!w.status && <Badge variant="outline">Closed</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono">{w.code}</div>
                  </TableCell>
                  <TableCell className="text-sm">{[w.address, w.city, w.state].filter(Boolean).join(", ") || "-"}</TableCell>
                  <TableCell className="text-right text-sm">{w.unit_count ?? 0}</TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="size-8" title="Edit" onClick={() => setDialog(w)}>
                          <Edit className="size-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="size-8" title={w.status ? "Close" : "Reopen"} onClick={() => setActive(w)}>
                          <Power className="size-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">
                    No warehouses yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && !saving && setDialog(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialog && dialog !== "new" ? `Edit ${dialog.name}` : "Add warehouse"}</DialogTitle>
            <DialogDescription>Stock is received into, and moved between, warehouses.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 py-2 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={set("name")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>Code</Label><Input value={form.code} onChange={set("code")} placeholder="Auto" className="h-10" /></div>
            </div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={set("address")} className="h-10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={set("city")} className="h-10" /></div>
              <div className="space-y-1.5"><Label>State</Label><Input value={form.state} onChange={set("state")} className="h-10" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} /></div>
            <label className="text-xs inline-flex items-center gap-2">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Use as the default warehouse for receipts
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="gap-1.5">
                {saving && <Loader2 className="size-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
